import {
  Box, Button, Divider, Flex, FormControl, FormLabel, Stack, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { Text14 } from "components/Text";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { useCallback, useState } from "react";
import DatePicker from "components/Form/DatePicker/BaseDatePicker";
import { PrimaryButton } from "components/Button";
import moment from "moment";
import NewHireJourneySingleSelect from "components/Form/Select/NewHireJourneySingleSelect";
import { useNewHiresListQuery } from "features/NewHireJourney/hooks";
import useCaminoStore from "hooks/useCaminoStore";
import { Task } from "features/Resources/Task/models";

interface CreateTaskFormProps {
  onSubmit: (values: Partial<Task>) => void;
  onCancel: () => void;
  submitIsLoading?: boolean;
}

export default function CreateTaskForm({
  onSubmit,
  onCancel,
  submitIsLoading = false,
}: CreateTaskFormProps) {
  const dateButtonColor = useColorModeValue("valueBlack.DEFAULT", "var(--chakra-colors-whiteAlpha-900)");
  const borderColor = useColorModeValue("gray.200", "white");
  const dateButtonBgHover = useColorModeValue("editableBackgroundGrey.light", "cdsGrey.700");

  const currentUser = useCaminoStore((state) => state.currentUser);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [newHireJourneyId, setNewHireJourneyId] = useState<string | undefined>();
  const [showJourneySelector, setShowJourneySelector] = useState(false);

  const { data: newHireJourneys } = useNewHiresListQuery("", { enabled: showJourneySelector });

  const handleSubmit = useCallback(() => {
    const taskData: Partial<Task> = {
      name,
      description,
      assigneeId: currentUser?.id,
      startOn: moment().toDate(),
      dueOn: dueDate,
      // Only include newHireJourneyId if it has a value
      ...(newHireJourneyId && { newHireJourneyId }),
    };
    onSubmit(taskData);
  }, [name, description, currentUser?.id, dueDate, newHireJourneyId, onSubmit]);

  const isValid = name.trim().length > 0;

  return (
    <Stack gap="4" mt="2">
      <HorizontalGreyBlackField label="Assignee">
        <Text14>{currentUser?.fullName || "You"}</Text14>
      </HorizontalGreyBlackField>

      <Divider />

      <FormControl isRequired>
        <FormLabel fontSize="14px">Task Name</FormLabel>
        <Textarea
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter task name"
          size="sm"
          resize="none"
          rows={2}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="14px">Description (Optional)</FormLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter task description"
          size="sm"
          resize="vertical"
          rows={4}
        />
      </FormControl>

      <Divider />

      <Box>
        <HorizontalGreyBlackField label="Due Date (Optional)" labelProps={{ minWidth: "120px" }}>
          <Box w="100%">
            <DatePicker
              minDate={moment().toDate()}
              onDateChange={(date) => setDueDate(date)}
              dateFormat="EEE, MMM d, yyyy"
              name="Due Date"
              value={dueDate}
              triggerButtonProps={{
                color: dateButtonColor,
                borderWidth: "1px",
                borderColor,
                borderRadius: "md",
                height: "40px",
                _hover: {
                  bg: dateButtonBgHover,
                  borderColor: "disabledGray.DEFAULT",
                },
              }}
              includeIcon={false}
              isClearable
            />
          </Box>
        </HorizontalGreyBlackField>
      </Box>

      <Divider />

      {!showJourneySelector ? (
        <Button
          variant="link"
          fontSize="14px"
          onClick={() => setShowJourneySelector(true)}
          alignSelf="flex-start"
        >
          + Link to New Hire Journey (optional)
        </Button>
      ) : (
        <Box>
          <HorizontalGreyBlackField label="New Hire Journey" labelProps={{ minWidth: "120px" }}>
            <Box w="100%">
              <NewHireJourneySingleSelect
                newHireJourneys={newHireJourneys || []}
                selectedNewHireJourneyId={newHireJourneyId || null}
                onChange={(id) => setNewHireJourneyId(id)}
                placeholder="Select a new hire journey"
                isClearable
                useBasicStyles
              />
            </Box>
          </HorizontalGreyBlackField>
        </Box>
      )}

      <Flex w="100%" justify="flex-end" gap="2" pt="4">
        <Button
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <PrimaryButton
          isLoading={submitIsLoading}
          isDisabled={!isValid}
          onClick={handleSubmit}
        >
          Create Task
        </PrimaryButton>
      </Flex>
    </Stack>
  );
}
